import { Controller } from '@hotwired/stimulus';
import { Uppy, DragDrop } from 'uppy';
import i18n from '../init/i18n';

export default class extends Controller {
  static targets = [
    'input',
    'emptyField',
    'filledField',
    'fileName',
    'fileSize',
    'cachedData',
    'removeFlag',
    'error',
    'fileCard',
    'statusMessage',
    'validationError',
    'defaultCardTemplate',
    'infoMessage'
  ]

  static values = {
    allowedTypes: Array,
    clickText: String,
    text: String,
    existingFileName: String,
    existingFileSize: Number,
    maxFileSize: { type: Number, default: 10485760 }, // 10 MB by default
    allowedMimeTypes: Array,
    fileExtensionsText: String
  }

  connect() {
    this.uppy = this.createUppy();

    if (this.hasExistingFileNameValue || this.inputTarget.files[0]) {
      this.showFileNameAndFilledIcon();
    } else if (this.hasCachedDataTarget && this.cachedDataTarget.value) {
      try {
        const cachedFileData = JSON.parse(this.cachedDataTarget.value);
        this.showFileNameAndFilledIcon({
          name: cachedFileData.metadata.filename,
          size: cachedFileData.metadata.size
        });
      } catch (e) {
        console.error('Failed to parse cached file data', e);
      }
    }

    this.boundClearHandler = this.#handleClear.bind(this);
    this.inputTarget.addEventListener('document-input:clear', this.boundClearHandler);
  }

  disconnect() {
    if (this.uppy) {
      this.uppy.close()
    }

    if (this.boundClearHandler) {
      this.inputTarget.removeEventListener('document-input:clear', this.boundClearHandler);
    }

    if (this.boundChangeHandler) {
      this.inputTarget.removeEventListener('change', this.boundChangeHandler);
    }
  }

  createUppy() {
    const uppy = new Uppy({
      autoProceed: false,
      locale: {
        strings: {
          exceedsSize: i18n.translations.fr.uppy.exceedsSize,
          youCanOnlyUploadFileTypes: i18n.translations.fr.uppy.youCanOnlyUploadFileTypes,
          youCanOnlyUploadX: {
            0: i18n.translations.fr.uppy.youCanOnlyUploadX['0'],
            1: i18n.translations.fr.uppy.youCanOnlyUploadX['1']
          }
        }
      },
      restrictions: {
        maxNumberOfFiles: 1,
        maxFileSize: this.maxFileSizeValue,
        allowedFileTypes: this.allowedMimeTypesValue
      }
    })

    uppy.use(DragDrop, {
      target: this.emptyFieldTarget,
      locale: {
        strings: {
          dropHereOr: `%{browse} ${this.textValue}` || 'Drop here or',
          browse: this.clickTextValue || 'browse'
        }
      }
    })

    this.#injectNoteText();

    uppy.on('file-added', (file) => {
      this.#handleFileAdded(file)
    })

    uppy.on('restriction-failed', (file, error) => {
      this.#handleRestrictionFailed(error);
    })

    return uppy
  }

  #handleFileAdded(file) {
    this.inputTarget.value = '';

    const existingFiles = this.uppy.getFiles();
    existingFiles.forEach(f => {
      if (f.id !== file.id) {
        this.uppy.removeFile(f.id);
      }
    });

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file.data)
    this.inputTarget.files = dataTransfer.files

    const event = new Event('change', { bubbles: true })
    this.inputTarget.dispatchEvent(event)

    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = 'false';
    }

    if (this.hasDefaultCardTemplateTarget) {
      const template = this.defaultCardTemplateTarget.content.cloneNode(true)
      this.filledFieldTarget.innerHTML = ''
      this.filledFieldTarget.appendChild(template)
    }

    this.showFileNameAndFilledIcon(file)
    this.#clearStatusAndValidation()
  }

  #handleClear() {
    this.emptyFieldTarget.classList.remove('d-none');
    this.filledFieldTarget.classList.add('d-none');

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('d-none');
      this.errorTarget.textContent = '';
    }

    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = '';
    }

    if (this.hasFileSizeTarget) {
      this.fileSizeTarget.textContent = '';
    }

    if (this.hasCachedDataTarget) {
      this.cachedDataTarget.value = '';
    }

    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = 'true';
    }

    if (this.uppy) {
      this.uppy.cancelAll()
    }

    this.#clearStatusAndValidation();
  }

  #handleRestrictionFailed(error) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = error.message;
      this.errorTarget.classList.remove('d-none');
    }
  }

  showFileNameAndFilledIcon(file) {
    this.emptyFieldTarget.classList.add('d-none');
    this.filledFieldTarget.classList.remove('d-none');

    if (this.hasInfoMessageTarget) {
      this.infoMessageTarget.classList.add('d-none');
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('d-none')
      this.errorTarget.textContent = ''
    }

    if (this.hasFileNameTarget) {
      if (file) {
        this.fileNameTarget.textContent = file.name
        if (this.hasFileSizeTarget) {
          this.fileSizeTarget.textContent = this.#formatFileSize(file.size)
        }
      } else if (this.hasExistingFileNameValue) {
        this.fileNameTarget.textContent = this.existingFileNameValue
        if (this.hasFileSizeTarget && this.hasExistingFileSizeValue) {
          this.fileSizeTarget.textContent = this.#formatFileSize(this.existingFileSizeValue)
        }
      }
    }
  }

  deleteFile() {
    this.inputTarget.value = '';

    if (this.uppy) {
      this.uppy.cancelAll();
    }

    this.emptyFieldTarget.classList.remove('d-none');
    this.filledFieldTarget.classList.add('d-none');

    if (this.hasInfoMessageTarget) {
      this.infoMessageTarget.classList.remove('d-none');
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('d-none');
      this.errorTarget.textContent = '';
    }

    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = '';
    }

    if (this.hasFileSizeTarget) {
      this.fileSizeTarget.textContent = '';
    }

    if (this.hasCachedDataTarget) {
      this.cachedDataTarget.value = '';
    }

    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = 'true';
    }

    this.#clearStatusAndValidation();
  }

  // Uppy's DragDrop plugin doesn't properly populate the note element via locale.strings.note
  // so we manually inject the text after the plugin is mounted
  #injectNoteText() {
    if (!this.hasFileExtensionsTextValue || !this.fileExtensionsTextValue) return;

    setTimeout(() => {
      const noteElement = this.emptyFieldTarget.querySelector('.uppy-DragDrop-note');
      if (noteElement) {
        noteElement.textContent = this.fileExtensionsTextValue;
      }
    }, 0);
  }

  #clearStatusAndValidation() {
    // Hide status and validation messages (styles are handled by the template)
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.classList.add('d-none')
    }

    if (this.hasValidationErrorTarget) {
      this.validationErrorTarget.classList.add('d-none');
    }
  }

  #formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }
}
